import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Divider,
  useToast,
  Alert,
  AlertIcon,
  Spinner,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
  Tooltip
} from '@chakra-ui/react';
import {
  MdDownload,
  MdWarning,
  MdCheckCircle,
  MdError
} from 'react-icons/md';
import documentService from '../services/documentService';

const RiskAnalysisModal = ({ isOpen, onClose, analysisData, precatorioId, state }) => {
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const toast = useToast();

  if (!analysisData) return null;

  const { riskAnalysis } = analysisData;

  // Get risk band color
  const getRiskColor = (band) => {
    switch (band) {
      case 'low':
        return 'green';
      case 'medium':
        return 'yellow';
      case 'high':
        return 'red';
      default:
        return 'gray';
    }
  };

  // Get risk band label
  const getRiskLabel = (band) => {
    switch (band) {
      case 'low':
        return 'BAIXO RISCO';
      case 'medium':
        return 'MÉDIO RISCO';
      case 'high':
        return 'ALTO RISCO';
      default:
        return 'DESCONHECIDO';
    }
  };

  // Get risk icon
  const getRiskIcon = (band) => {
    switch (band) {
      case 'low':
        return MdCheckCircle;
      case 'medium':
        return MdWarning;
      case 'high':
        return MdError;
      default:
        return MdWarning;
    }
  };

  // Handle PDF download
  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      await documentService.downloadRiskAnalysisPDF(state, precatorioId);
      toast({
        title: 'Download iniciado',
        description: 'O relatório está sendo baixado.',
        status: 'success',
        duration: 3000
      });
    } catch (error) {
      toast({
        title: 'Erro ao baixar relatório',
        description: error.message || 'Não foi possível baixar o relatório PDF',
        status: 'error',
        duration: 5000
      });
    } finally {
      setDownloadingPdf(false);
    }
  };

  const riskColor = getRiskColor(riskAnalysis.band);
  const riskLabel = getRiskLabel(riskAnalysis.band);
  const RiskIcon = getRiskIcon(riskAnalysis.band);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalContent maxW="90vw">
        <ModalHeader>
          Análise de Risco - Processo de Execução
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={6} align="stretch">
            
            {/* Risk Level Header */}
            <Box 
              p={6} 
              borderRadius="lg" 
              borderWidth="2px" 
              borderColor={`${riskColor}.400`}
              bg={`${riskColor}.50`}
            >
              <VStack spacing={3}>
                <HStack spacing={3}>
                  <Icon as={RiskIcon} boxSize={8} color={`${riskColor}.600`} />
                  <Text fontSize="3xl" fontWeight="bold" color={`${riskColor}.700`}>
                    {riskLabel}
                  </Text>
                </HStack>
                <Text fontSize="xl" color="gray.700">
                  <strong>Pontuação Total:</strong> {riskAnalysis.document_score}
                </Text>
              </VStack>
            </Box>

            {/* Statistics Summary */}
            <Box>
              <Text fontSize="lg" fontWeight="bold" mb={3} color="gray.700">
                Estatísticas Gerais
              </Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <Stat 
                  p={4} 
                  borderWidth="1px" 
                  borderRadius="md" 
                  bg="white"
                  boxShadow="sm"
                >
                  <StatLabel>Total de Ocorrências</StatLabel>
                  <StatNumber color="blue.600">{riskAnalysis.total_hits || 0}</StatNumber>
                  <StatHelpText>Termos de risco encontrados</StatHelpText>
                </Stat>
                
                <Stat 
                  p={4} 
                  borderWidth="1px" 
                  borderRadius="md" 
                  bg="white"
                  boxShadow="sm"
                >
                  <StatLabel>Termos Únicos</StatLabel>
                  <StatNumber color="purple.600">
                    {Object.keys(riskAnalysis.per_term_counts || {}).length}
                  </StatNumber>
                  <StatHelpText>Tipos diferentes de risco</StatHelpText>
                </Stat>
                
                <Stat 
                  p={4} 
                  borderWidth="1px" 
                  borderRadius="md" 
                  bg="white"
                  boxShadow="sm"
                >
                  <StatLabel>Páginas Analisadas</StatLabel>
                  <StatNumber color="teal.600">{riskAnalysis.total_pages || 0}</StatNumber>
                  <StatHelpText>Documento completo</StatHelpText>
                </Stat>
              </SimpleGrid>
            </Box>

            <Divider />

            {/* Term Counts */}
            {riskAnalysis.per_term_counts && Object.keys(riskAnalysis.per_term_counts).length > 0 && (
              <Box>
                <Text fontSize="lg" fontWeight="bold" mb={3} color="gray.700">
                  Contagem por Termo de Risco
                </Text>
                <Box 
                  maxH="300px" 
                  overflowY="auto" 
                  borderWidth="1px" 
                  borderRadius="md"
                  bg="white"
                  position="relative"
                  zIndex={1}
                >
                  <Table size="sm" variant="simple">
                    <Thead position="sticky" top={0} bg="gray.50" zIndex={2}>
                      <Tr>
                        <Th>Termo de Risco</Th>
                        <Th isNumeric>Ocorrências</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {Object.entries(riskAnalysis.per_term_counts)
                        .sort((a, b) => b[1] - a[1])
                        .map(([term, count]) => (
                          <Tr key={term}>
                            <Td>{term}</Td>
                            <Td isNumeric>
                              <Badge colorScheme="orange">{count}</Badge>
                            </Td>
                          </Tr>
                        ))}
                    </Tbody>
                  </Table>
                </Box>
              </Box>
            )}

            <Divider />

            {/* Detailed Hits Table */}
            {riskAnalysis.hits && riskAnalysis.hits.length > 0 && (
              <Box>
                <Text fontSize="lg" fontWeight="bold" mb={3} color="gray.700">
                  Detalhamento das Ocorrências ({riskAnalysis.hits.length})
                </Text>
                <Box 
                  maxH="400px" 
                  overflowY="auto" 
                  borderWidth="1px" 
                  borderRadius="md"
                  bg="white"
                  position="relative"
                  zIndex={1}
                >
                  <Table size="sm" variant="striped">
                    <Thead position="sticky" top={0} bg="gray.50" zIndex={2}>
                      <Tr>
                        <Th>Termo</Th>
                        <Th isNumeric>Página</Th>
                        <Th>Trecho</Th>
                        <Th isNumeric>Peso</Th>
                        <Th isNumeric>Pontuação</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {riskAnalysis.hits.map((hit, index) => (
                        <Tr key={index}>
                          <Td>
                            <Text fontSize="sm" fontWeight="medium" color="gray.700">
                              {hit.term}
                            </Text>
                            {hit.has_negation && (
                              <Tooltip label="Negação detectada - peso reduzido">
                                <Badge colorScheme="blue" fontSize="xs" ml={1}>
                                  NEG
                                </Badge>
                              </Tooltip>
                            )}
                            {hit.in_header && (
                              <Tooltip label="Encontrado no cabeçalho - peso aumentado">
                                <Badge colorScheme="purple" fontSize="xs" ml={1}>
                                  HEADER
                                </Badge>
                              </Tooltip>
                            )}
                          </Td>
                          <Td isNumeric>
                            <Badge colorScheme="gray">{hit.page}</Badge>
                          </Td>
                          <Td>
                            <Text fontSize="xs" color="gray.600" maxW="300px" isTruncated>
                              {hit.snippet}
                            </Text>
                          </Td>
                          <Td isNumeric>
                            <Text fontSize="sm" color="gray.700">
                              {hit.weight}
                            </Text>
                          </Td>
                          <Td isNumeric>
                            <Badge colorScheme={hit.score >= 5 ? 'red' : hit.score >= 3 ? 'yellow' : 'green'}>
                              {hit.score}
                            </Badge>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </Box>
            )}

            {/* No hits message */}
            {(!riskAnalysis.hits || riskAnalysis.hits.length === 0) && (
              <Alert status="success">
                <AlertIcon />
                Nenhum termo de risco foi encontrado no documento!
              </Alert>
            )}

            {/* Analysis timestamp */}
            {riskAnalysis.analyzedAt && (
              <Text fontSize="sm" color="gray.500" textAlign="right">
                Análise realizada em: {new Date(riskAnalysis.analyzedAt).toLocaleString('pt-BR')}
              </Text>
            )}

          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button 
            leftIcon={downloadingPdf ? <Spinner size="sm" /> : <MdDownload />}
            colorScheme="blue" 
            mr={3}
            onClick={handleDownloadPdf}
            isDisabled={downloadingPdf}
          >
            {downloadingPdf ? 'Gerando PDF...' : 'Baixar Relatório PDF'}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RiskAnalysisModal;
